import React, { ReactNode, FunctionComponent, Fragment } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { Icon } from 'mdlkit';
import { FocusShadowField } from 'components/shared/Styles';
import { AutocompleteInput } from '../../../core';
import PopularAffiliationMenu from './PopularAffiliationMenu';
import AffiliationMenu from './AffiliationMenu';
import { AffiliationSearch } from '../../../interfaces/register';
import { required } from '../../../utils/shared/validations';
import { event } from '../../../lib/analytics';
import { DTC, CSV } from '../../../constants/register';
import { REGISTRATION } from '../../../constants/events';

export interface AffiliationOptions {
  isPopular: boolean;
  affiliations: AffiliationSearch[] | null;
}

interface Props {
  affiliationOptions: AffiliationOptions;
  stateId: number | null;
  errorMessage?: string;
  id: string;
  label?: string;
  noMatchError?: ReactNode;
  onInputValueChange?(value: string): void;
  placeholder?: string;
  pt?: string;
  showNoMatch: boolean;
  toggleMemberIdForm(boolean): void;
  setShowSkipLink(boolean): void;
}

const AffiliationSearchField: FunctionComponent<Props> = ({
  affiliationOptions,
  stateId,
  id,
  errorMessage,
  label,
  noMatchError,
  onInputValueChange,
  placeholder,
  pt,
  showNoMatch,
  toggleMemberIdForm,
  setShowSkipLink,
}: Props) => {
  const { isPopular, affiliations } = affiliationOptions;
  const items = affiliations || [];

  const getSelectedAffiliation = (
    selectedAffiliationName: string
  ): AffiliationSearch | void => {
    return affiliations && affiliations.length > 0
      ? affiliations.filter(
          (aff: AffiliationSearch) => aff.label === selectedAffiliationName
        )[0]
      : undefined;
  };

  const menu = isPopular ? (
    <PopularAffiliationMenu affiliations={items} />
  ) : (
    <AffiliationMenu affiliations={items} stateId={stateId} />
  );

  return (
    <Fragment>
      <FormSpy>
        {({ form }) => (
          <FocusShadowField
            id={id}
            name="affiliationName"
            items={items}
            menu={menu}
            render={AutocompleteInput}
            autoCorrect="off"
            spellCheck={false}
            onInputValueChange={onInputValueChange}
            onSelect={(val) => {
              const selectedAffiliation = getSelectedAffiliation(val);
              if (typeof selectedAffiliation !== 'undefined') {
                event({
                  action: 'select_affiliation',
                  category: REGISTRATION,
                  parameters: { affiliationId: selectedAffiliation.id },
                });
                form.change('affiliationId', selectedAffiliation.id);
                form.change('affiliationType', selectedAffiliation.type);
                toggleMemberIdForm(
                  selectedAffiliation.type === DTC ||
                    selectedAffiliation.type === CSV
                );
                setShowSkipLink(selectedAffiliation.type !== DTC);
              }
            }}
            validate={required}
            icon={<Icon baseUrl="/static/svg" name="search" size={20} />}
            placeholder={placeholder}
            label={label}
            pt={pt}
            errorMessage={errorMessage}
            openOnFocus
          />
        )}
      </FormSpy>
      <Field
        name="affiliationId"
        type="hidden"
        component="input"
        validate={required}
      />
      <Field
        name="affiliationType"
        type="hidden"
        component="input"
        validate={required}
      />
      {showNoMatch ? noMatchError : null}
    </Fragment>
  );
};

export default AffiliationSearchField;
